import { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, Badge, SearchableSelect } from '@/components/common';
import { QuoteCard } from '@/components/quotes/QuoteCard';
import { ProjectedXpBadge } from '@/components/quotes/ProjectedXpBadge';
import { useAuthStore } from '@/store/auth';
import { useQuotesForRequest } from '@/hooks/useQuotes';
import { optionExtras } from './proposalFlow';
import { ShareCustomerLink } from './ShareCustomerLink';
import type { QuoteSubmitResponse } from '@/types';

type SortOption = 'premium_low' | 'premium_high';

const sortOptions = [
  { value: 'premium_low', label: 'Premium: Low to High' },
  { value: 'premium_high', label: 'Premium: High to Low' },
];

/**
 * BOARD #245 part 3, on the screen where the merge is visible.
 *
 * This list carries BOTH kinds of option, because there is only one kind of
 * row: `apps.integrations.tasks._persist_quote` (insurer API) and
 * `QuoteOptionCreateSerializer` (a broker typing a figure in) both write a
 * `Quote(status="received")` onto the same `QuoteRequest`. They are not
 * alternatives and never were.
 *
 * WHAT THE BROKER COULD NOT SEE was which is which, and it decides what
 * happens next: an insurer can only be asked to issue against an application
 * it knows about, so a hand-entered option has no `insurer_quote_id` and
 * `PaymentInitiateView` answers "No insurer application/proposal id available
 * for this quote". That refusal used to arrive three screens later. It is now
 * on the card.
 *
 * Both kinds still lead to the proposal step, deliberately -- a hand-entered
 * option's proposal IS recorded and DOES reach the issued policy through the
 * document-upload path (board #255). Hiding the button would remove working
 * behaviour; the honest fix was to say up front what the next screens can and
 * cannot do.
 */
export function QuoteComparisonPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [sortBy, setSortBy] = useState<SortOption>('premium_low');
  // Messaging Plan 2: an agent sees each option's projected XP; nobody else fetches it.
  const isPosp = useAuthStore((state) => ['posp', 'super_posp'].includes(state.user?.role ?? ''));

  // BROKER-2: quote pricing runs synchronously during the submit POST, so the
  // wizard already knows the outcome. Using it means a request with no
  // adapter-backed or eligible insurer resolves instantly and says WHY,
  // instead of spinning for the full poll timeout and then showing a bare
  // "No quotes available". Absent (e.g. the page was opened directly from the
  // quotes list) we fall back to polling alone.
  const submitResult = (location.state as { submitResult?: QuoteSubmitResponse } | null)?.submitResult;
  const dispatchSettled =
    submitResult?.status === 'no_quotes_available' || submitResult?.quotes_created === 0;

  const { quotes, isComplete: pollComplete } = useQuotesForRequest(requestId ?? null);
  const isComplete = pollComplete || dispatchSettled;

  const sortedQuotes = useMemo(() => {
    if (!quotes) return [];
    const sorted = [...quotes];
    if (sortBy === 'premium_low') {
      sorted.sort((a, b) => (a.premium_total ?? 0) - (b.premium_total ?? 0));
    } else {
      sorted.sort((a, b) => (b.premium_total ?? 0) - (a.premium_total ?? 0));
    }
    // THE BROKER'S OWN PICK OUTRANKS THE PREMIUM SORT. `Quote.is_recommended`
    // is set from the checkbox on QuotePremiumOptionsCard and, until this line,
    // had no reader anywhere in the frontend: the flag was stored, the card
    // said "Option updated", and nothing ever changed on any screen. Ordering
    // here is what makes that control mean something.
    //
    // Array.prototype.sort is stable, so the premium order the broker chose
    // survives intact inside each of the two groups.
    sorted.sort(
      (a, b) => Number(optionExtras(b).isRecommended) - Number(optionExtras(a).isRecommended)
    );
    return sorted;
  }, [quotes, sortBy]);

  const handleSelectQuote = (insurerCode: string, quoteId?: string) => {
    // The option's own id rides along: one insurer can price this request up
    // to three ways (one row per cover), and the code alone is ambiguous.
    const exact = quoteId ? `?quote=${encodeURIComponent(quoteId)}` : '';
    navigate(`/quotes/proposal/${requestId}/${insurerCode}${exact}`);
  };

  const hasQuotes = sortedQuotes.length > 0;

  return (
    <div>
      <PageHeader
        title="Compare Quotes"
        subtitle={`Request: ${requestId}`}
        breadcrumbs={[
          { label: 'Quotes', href: '/quotes' },
          { label: 'Compare' },
        ]}
        actions={
          <Button
            variant="outline"
            leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
            onClick={() => navigate('/quotes')}
          >
            Back to Quotes
          </Button>
        }
      />

      {/* BOARD #340: choosing a tier still opens the internal proposal page; this
          hands the customer their own quote page. */}
      {hasQuotes && requestId && (
        <div className="mb-4">
          <ShareCustomerLink requestId={requestId} />
        </div>
      )}

      {/* Status Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Badge variant={isComplete ? (hasQuotes ? 'success' : 'error') : 'warning'}>
            {isComplete ? (hasQuotes ? 'All quotes received' : 'No quotes available') : 'Fetching quotes...'}
          </Badge>
          {!isComplete && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <svg className="animate-spin h-4 w-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Fetching quotes...
            </div>
          )}
        </div>
        <div className="w-56">
          <SearchableSelect
            value={sortBy}
            onChange={(value) => setSortBy(value as SortOption)}
            options={sortOptions}
          />
        </div>
      </div>

      {/* Complete Banner */}
      {isComplete && hasQuotes && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-300 font-medium">All quotes received. Compare and select your preferred plan.</p>
        </div>
      )}

      {/* Quote Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Actual quote cards */}
        {sortedQuotes.map((quote) => {
          const { pricingSource, isRecommended } = optionExtras(quote);
          return (
            <div
              key={quote.id ?? quote.insurer_code}
              data-testid={`compare-option-${quote.insurer_code}`}
              className="flex flex-col gap-2"
            >
              {/* The other half of the ordering above: which one it is, said
                  out loud. A card that is merely first is indistinguishable
                  from the cheapest. */}
              {isRecommended && (
                <div className="flex items-center gap-2">
                  <Badge variant="success" size="sm">
                    Recommended
                  </Badge>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Marked on the quote, so it leads the list.
                  </span>
                </div>
              )}
              <QuoteCard quote={quote} onSelect={() => handleSelectQuote(quote.insurer_code, quote.id)} />
              {isPosp && requestId && (
                <div className="px-1">
                  <ProjectedXpBadge quoteRequestId={requestId} quoteId={quote.id} />
                </div>
              )}
              {pricingSource === 'manual' ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                  Entered by hand. The insurer has no application to issue against, so this one
                  cannot go through their payment gateway — take the payment as usual and upload the
                  issued policy copy on the quote.
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                  Priced by the insurer&apos;s API, so it can be taken through proposal, payment and
                  issuance.
                </p>
              )}
            </div>
          );
        })}

        {/* Loading skeleton cards */}
        {!isComplete && Array.from({ length: 3 }).map((_, i) => (
          <Card key={`skeleton-${i}`}>
            <div className="animate-pulse space-y-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mt-2" />
                </div>
              </div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/5" />
              </div>
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </Card>
        ))}
      </div>

      {/* Empty state: no eligible/adapter-backed insurer produced a quote for
          this request. This is expected — not an error — whenever the
          product line has no live adapter, or the broker isn't configured
          for the insurer(s) that do (see backend/apps/quotes/CLAUDE.md /
          item-04-diagnosis.md's config prerequisite). */}
      {isComplete && !hasQuotes && (
        <Card className="mt-6">
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">No quotes available</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {submitResult?.message ??
                "No insurers were able to provide a quote for this request — this insurer/product combination may be awaiting integration, or isn't yet enabled for your broker."}
            </p>
            {/* The server tells us how many insurers it actually queried, which
                distinguishes "no insurer configured for your broker" from
                "queried them and none priced it". */}
            {submitResult?.insurers_queried !== undefined && (
              <p className="text-xs text-gray-400 mt-2">
                {submitResult.insurers_queried === 0
                  ? 'No eligible insurer is configured for your broker on this product line.'
                  : `${submitResult.insurers_queried} insurer(s) queried, none returned a price.`}
              </p>
            )}
            <Button className="mt-4" onClick={() => navigate('/quotes/new')}>
              New Quote Request
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
